import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import BootstrapTable from 'react-bootstrap-table-next';
import axios from 'axios';
import * as S from '../Advances/AdvancesStyles';
import { toast } from 'react-toastify';
import { createPollinationProject, getPollinationProjects, updatePollinationProject } from '../../../../services/pollinationService';
import { IndianStates, fetchDistrictsByStateName } from '../../../../constants/pollinationLocation/StateDistrictData';
import { fetchBlockByDistrictName } from '../../../../constants/pollinationLocation/Blocks';
import { POLLINATION_CROPS } from '../../../../constants/pollinationCrops';

/**
 * Step one of the Pollination module web portal: create a project and assign the
 * field agents who can see it on their phone, and edit either afterward. Farmer
 * list / drill-down comes later once the mobile side exists to actually populate
 * farmers under a project — see the implementation plan's suggested build order.
 */
const emptyForm = {
  name: '',
  state: '',
  district: '',
  block: '',
  key_crop: '',
  partners: '',
  members: [],
};

// State/district/block use the exact same source data as the mobile app's own
// cascading pickers (copied from beekind-fieldview/src/constants — plain JS
// data, no React Native dependency). Creatable, not a locked Select — a
// state/district/block not in the list can still be typed in directly, same
// "dropdown with manual fallback" rule the mobile app already follows for its
// own free-text fields.
const STATE_OPTIONS = (IndianStates || []).map((s) => ({ value: s.name, label: s.name }));
const CROP_OPTIONS = POLLINATION_CROPS.map((c) => ({ value: c, label: c }));

// A project can have several key crops. The server stores them in the existing
// key_crop string, comma separated ("Fennel, Sunflower"), so no backend change.
const splitCrops = (value) => (value || '').split(',').map((c) => c.trim()).filter(Boolean);
const joinCrops = (crops) => Array.from(new Set(crops.flatMap((c) => splitCrops(c)))).join(', ');

const PollinationProjects = ({ history }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [blockOptions, setBlockOptions] = useState([]);
  const [viewingMembers, setViewingMembers] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  const loadProjects = async () => {
    setLoading(true);
    const data = await getPollinationProjects();
    setProjects(data);
    setLoading(false);
  };

  // Project "members" are regular FieldView mobile-app users, not just the
  // field_view_app_admin ones the existing Projects section's dropdown fetches
  // — field_view_app_user is the other real FieldView role (see SplashScreen.js's
  // own login gate, which checks both). getAllUsers only accepts one role per
  // call and the redux slice it writes to overwrites rather than merges on each
  // dispatch, so both roles are fetched directly and merged here instead.
  //
  // Each role is fetched and merged independently (not Promise.all) — this
  // backend/network has been flaky in testing, and a fail-fast Promise.all
  // meant a single transient failure on either request blanked out ALL member
  // names (even ones from the role that succeeded), which is what caused names
  // to intermittently show as raw IDs.
  const mergeUsers = (incoming) => {
    setAllUsers((prev) => {
      const seen = new Set(prev.map((u) => u.userInfo?._id));
      const additions = (incoming || []).filter((u) => u.userInfo?._id && !seen.has(u.userInfo._id));
      return [...prev, ...additions];
    });
  };

  const fetchUsersByRole = async (role) => {
    try {
      // Cache-busting _=Date.now() is required: on a repeat page load the
      // browser sends a conditional GET and the server answers 304 Not
      // Modified — axios's XHR adapter then hands back an EMPTY body even
      // though the browser's cache has the real one, which silently wiped
      // out every name every other reload. A unique query param per request
      // makes it a new URL each time, so the browser never serves a 304 for it.
      const res = await axios.get(`${process.env.REACT_APP_HUMBLEBEE_API_URL}/users?limit=10000&role=${role}&_=${Date.now()}`);
      mergeUsers(res.data?.userInfo);
    } catch (error) {
      console.error(`Error fetching ${role} users:`, error);
    }
  };

  const loadFieldAgentUsers = () => {
    setAllUsers([]);
    fetchUsersByRole('field_view_app_admin');
    fetchUsersByRole('field_view_app_user');
  };

  useEffect(() => {
    loadProjects();
    loadFieldAgentUsers();
  }, []);

  const userNameById = (id) => (allUsers || []).find((u) => u.userInfo._id === id)?.userInfo?.name || id;

  const handleField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleStateChange = (opt) => {
    const stateName = opt?.value || '';
    handleField('state', stateName);
    handleField('district', '');
    handleField('block', '');
    setDistrictOptions(stateName ? fetchDistrictsByStateName(stateName).map((d) => ({ value: d.name, label: d.name })) : []);
    setBlockOptions([]);
  };

  const handleDistrictChange = (opt) => {
    const districtName = opt?.value || '';
    handleField('district', districtName);
    handleField('block', '');
    setBlockOptions(districtName ? fetchBlockByDistrictName(districtName).map((b) => ({ value: b.name, label: b.name })) : []);
  };

  const openCreateModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDistrictOptions([]);
    setBlockOptions([]);
    setShowModal(true);
  };

  const openEditModal = (project) => {
    setEditingId(project._id);
    setForm({
      name: project.name || '',
      state: project.location?.state || '',
      district: project.location?.district || '',
      block: project.location?.block || '',
      key_crop: project.key_crop || '',
      partners: (project.partners || []).join(', '),
      members: (project.members || []).map((id) => ({ value: id, label: userNameById(id) })),
    });
    setDistrictOptions(project.location?.state ? fetchDistrictsByStateName(project.location.state).map((d) => ({ value: d.name, label: d.name })) : []);
    setBlockOptions(project.location?.district ? fetchBlockByDistrictName(project.location.district).map((b) => ({ value: b.name, label: b.name })) : []);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        location: { state: form.state, district: form.district, block: form.block },
        key_crop: form.key_crop,
        partners: form.partners ? form.partners.split(',').map(p => p.trim()).filter(Boolean) : [],
        members: form.members.map(m => m.value),
      };
      if (editingId) {
        await updatePollinationProject(editingId, payload);
        toast.success('Pollination project updated');
      } else {
        await createPollinationProject(payload);
        toast.success('Pollination project created');
      }
      setShowModal(false);
      setForm(emptyForm);
      setEditingId(null);
      loadProjects();
    } catch (error) {
      console.error('Error saving pollination project:', error);
      toast.error(error.response?.data?.message || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      dataField: 'name',
      text: 'Project',
      sort: true,
      formatter: (name, row) => (
        <span style={{ color: '#2563EB', cursor: 'pointer', fontWeight: 600 }} onClick={() => history.push(`/pollination-projects/${row._id}`)}>
          {name}
        </span>
      ),
    },
    {
      dataField: 'location',
      text: 'Location',
      formatter: (loc) => [loc?.block, loc?.district, loc?.state].filter(Boolean).join(', ') || '—',
    },
    { dataField: 'key_crop', text: 'Key Crop', formatter: (v) => v || '—' },
    {
      dataField: 'partners',
      text: 'Partners',
      formatter: (v) => (v || []).join(', ') || '—',
    },
    {
      dataField: 'members',
      text: 'Members',
      formatter: (m, row) => {
        const members = m || [];
        if (members.length === 0) return '—';
        const names = members.map(userNameById);
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{members.length} member{members.length === 1 ? '' : 's'}</div>
            <div style={{
              fontSize: 12,
              color: '#6B7280',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 180,
            }}>
              {names.join(', ')}
            </div>
            <span
              style={{ color: '#2563EB', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
              onClick={() => setViewingMembers({ projectName: row.name, names })}
            >
              Show all
            </span>
          </div>
        );
      },
    },
    {
      dataField: 'status',
      text: 'Status',
      formatter: (v) => <S.Pill variant={v === 'ACTIVE' ? 'approved' : 'rejected'}>{v}</S.Pill>,
    },
    {
      dataField: '_id',
      text: '',
      formatter: (id, row) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <S.Btn ghost sm onClick={() => openEditModal(row)}>Edit</S.Btn>
          <S.Btn ghost sm onClick={() => history.push(`/pollination-projects/${id}`)}>View Farmers</S.Btn>
        </div>
      ),
    },
  ];

  return (
    <S.MainContainer>
      <S.TopBar>
        <div>
          <h1>Pollination Projects</h1>
          <p>Create a pollination project and assign the field agents who can work on it.</p>
        </div>
        <S.Btn onClick={openCreateModal}>+ New Project</S.Btn>
      </S.TopBar>

      <S.TableWrap>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading…</div>
        ) : (
          <BootstrapTable
            // Remounts once allUsers finishes loading — BootstrapTable only
            // re-renders rows when `data` changes reference, not when a
            // closure (userNameById) used inside a formatter changes, so
            // without this the Members column can permanently freeze on
            // whatever it first painted (raw IDs, before allUsers arrived).
            key={allUsers.length}
            keyField="_id"
            data={projects}
            columns={columns}
            bordered={false}
            hover
            noDataIndication={() => (
              <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>
                No pollination projects yet — create one to get started.
              </div>
            )}
          />
        )}
      </S.TableWrap>

      <S.ModalOverlay show={showModal} onClick={() => setShowModal(false)}>
        <S.ModalContent onClick={e => e.stopPropagation()}>
          <h2>{editingId ? 'Edit Pollination Project' : 'New Pollination Project'}</h2>

          <div style={{ marginTop: 16 }}>
            <label>Project Name*</label>
            <input
              type="text"
              placeholder="e.g. Fennel pollination - Dhar"
              value={form.name}
              onChange={e => handleField('name', e.target.value)}
              style={{ width: '100%', padding: 8, marginTop: 4 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
            <div style={{ flex: 1 }}>
              <label>State</label>
              <CreatableSelect
                value={form.state ? { value: form.state, label: form.state } : null}
                options={STATE_OPTIONS}
                onChange={handleStateChange}
                placeholder="Select or type a state"
                maxMenuHeight={190}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                menuPortalTarget={document.body}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label>District</label>
              <CreatableSelect
                value={form.district ? { value: form.district, label: form.district } : null}
                options={districtOptions}
                onChange={handleDistrictChange}
                placeholder="Select or type a district"
                maxMenuHeight={190}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                menuPortalTarget={document.body}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label>Block</label>
              <CreatableSelect
                value={form.block ? { value: form.block, label: form.block } : null}
                options={blockOptions}
                onChange={(opt) => handleField('block', opt?.value || '')}
                placeholder="Select or type a block"
                maxMenuHeight={190}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                menuPortalTarget={document.body}
              />
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <label>Key Crop</label>
            <CreatableSelect
              isMulti
              value={splitCrops(form.key_crop).map((c) => ({ value: c, label: c }))}
              options={CROP_OPTIONS}
              onChange={(opts) => handleField('key_crop', joinCrops((opts || []).map((o) => o.value)))}
              placeholder="Select or type one or more crops"
              maxMenuHeight={190}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              menuPortalTarget={document.body}
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <label>Partners (comma separated)</label>
            <input type="text" value={form.partners} onChange={e => handleField('partners', e.target.value)} style={{ width: '100%', padding: 8, marginTop: 4 }} />
          </div>

          <div style={{ marginTop: 12 }}>
            <label>Assign Field Agents (Members)</label>
            <Select
              isMulti
              value={form.members}
              options={(allUsers || []).map(u => ({ value: u.userInfo._id, label: u.userInfo.name }))}
              onChange={selected => handleField('members', selected || [])}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              menuPortalTarget={document.body}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            <S.Btn ghost sm onClick={() => setShowModal(false)}>Cancel</S.Btn>
            <S.Btn sm onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Project'}
            </S.Btn>
          </div>
        </S.ModalContent>
      </S.ModalOverlay>

      <S.ModalOverlay show={!!viewingMembers} onClick={() => setViewingMembers(null)}>
        <S.ModalContent onClick={e => e.stopPropagation()}>
          <h2>{viewingMembers?.projectName} — Members</h2>
          <div style={{ marginTop: 16, maxHeight: 320, overflowY: 'auto' }}>
            {(viewingMembers?.names || []).map((name, idx) => (
              <div key={idx} style={{ padding: '10px 0', borderBottom: '1px solid #F0F0F0' }}>{name}</div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <S.Btn ghost sm onClick={() => setViewingMembers(null)}>Close</S.Btn>
          </div>
        </S.ModalContent>
      </S.ModalOverlay>
    </S.MainContainer>
  );
};

export default PollinationProjects;
